import axios from 'axios';
import toast from 'react-hot-toast';

/**
 * SLIM ERP Core API Client - Base Configuration
 * 
 * This file contains ONLY core API configuration and base authentication/user APIs.
 * Specific domain APIs have been moved to dedicated files for better organization:
 * 
 * - adminApi.js: Location management and administrative operations
 * - rbacApi.js: Role-based access control (roles, permissions, user roles)
 * - inventoryApi.js: Complete inventory management system
 * - financeApi.js: Financial operations (if exists)
 * - crmApi.js: Customer relationship management (if exists)
 * 
 * Architecture:
 * - API Gateway (port 3001): Routes requests and handles authentication
 * - Auth Service (port 3002): Handles auth + user management + admin operations
 * - Client (port 3000): React frontend with modern UI
 * - Redis (port 6379): Session management and caching
 * 
 * Last Updated: December 15, 2025 - Fixed Vite environment variable access
 */

// API Configuration - Using Vite's import.meta.env instead of process.env
const API_BASE_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:3001/api';
const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';

/**
 * Resolve the active location id for the X-Location-Id header.
 *
 * LocationContext persists the selection to three places (localStorage
 * 'X-Location-Id', localStorage 'lastUsedLocationId' and the
 * 'currentLocationId' cookie) and reads them back in that order of
 * preference. The interceptor has to look in the same places: a session that
 * only carries 'lastUsedLocationId' (older builds, partially cleared storage,
 * a restored tab) would otherwise send no location header at all and every
 * location-scoped write would fail with "Location is required".
 */
const resolveLocationId = () => {
  try {
    const fromStorage = localStorage.getItem('X-Location-Id')
      || localStorage.getItem('lastUsedLocationId');
    if (fromStorage) return fromStorage;

    const cookieMatch = document.cookie
      .split('; ')
      .find((row) => row.startsWith('currentLocationId='));
    return cookieMatch ? decodeURIComponent(cookieMatch.split('=')[1]) : null;
  } catch (error) {
    return null;
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests (important for HTTP-only cookies)
});

// Request interceptor to add authorization and location context
api.interceptors.request.use(
  (config) => {
    // Check if this is a public route that doesn't need authentication
    const isPublicRoute = config.url?.includes('/public');

    // Get access token from session storage and add to Authorization header (skip for public routes)
    if (!isPublicRoute) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      if (accessToken) {
        if (sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) !== accessToken) {
          sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
        }
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    // Add location context header if available (using localStorage for persistence)
    const locationId = resolveLocationId();
    if (locationId) {
      config.headers['X-Location-Id'] = locationId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh promise for stampede protection
let refreshPromise = null;

// Response interceptor to handle errors and token refresh with throttling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const isAuthRequest = originalRequest.url?.includes('/auth/');
    const isPublicRoute = originalRequest.url?.includes('/public');

    // Handle 401 (unauthorized) for non-auth, non-public requests (token expiry)
    if (status === 401 && !originalRequest._retry && !isAuthRequest && !isPublicRoute) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post(
            `${API_BASE_URL}/auth/refresh-token`,
            {},
            { withCredentials: true }
          ).finally(() => { refreshPromise = null; });
        }
        const refreshResponse = await refreshPromise;
        const newAccessToken = refreshResponse.data?.data?.accessToken;
        const sessionIdleTimeoutMs = refreshResponse.data?.data?.sessionIdleTimeoutMs;
        if (newAccessToken) {
          // Guard against stale refresh-token cookies belonging to a different user.
          // sessionUserId stores the logged-in user's email (a stable plain-string key).
          const refreshedUser = refreshResponse.data?.data?.user;
          const sessionUserId = (sessionStorage.getItem('sessionUserId') || '').trim().toLowerCase();
          if (refreshedUser && sessionUserId) {
            const refreshedEmail = (refreshedUser.email || refreshedUser.profile?.email || '').trim().toLowerCase();
            if (sessionUserId && refreshedEmail && refreshedEmail !== sessionUserId) {
              // Different user returned – treat as expired to force re-login
              sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
              localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
              sessionStorage.removeItem('sessionUserId');
              localStorage.removeItem('userPermissions');
              window.dispatchEvent(new CustomEvent('auth:expired'));
              return Promise.reject(new Error('Session user mismatch after token refresh'));
            }
          }
          sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, newAccessToken);
          localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, newAccessToken);
          window.dispatchEvent(new CustomEvent('auth:session-extended', {
            detail: { sessionIdleTimeoutMs }
          }));
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } else {
          throw new Error('No access token in refresh response');
        }
      } catch (refreshError) {
        // Only a definitive answer from the auth service ends the session:
        //   400 - no refresh cookie at all
        //   401 - refresh cookie invalid or expired
        //   403 - account no longer active
        // A network error, a gateway 502/503/504 (service restarting during a
        // deploy) or a 429 says nothing about the session, and tearing it down
        // there logs working users out at random. Those are surfaced to the
        // caller instead, and the next refresh attempt retries.
        const refreshStatus = refreshError?.response?.status;
        const sessionIsOver = refreshStatus === 400
          || refreshStatus === 401
          || refreshStatus === 403;

        if (!sessionIsOver) {
          return Promise.reject(refreshError);
        }

        // Dispatch auth expired event for React Router navigation handler
        sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem('sessionUserId');
        localStorage.removeItem('userPermissions');
        window.dispatchEvent(new CustomEvent('auth:expired'));
        return Promise.reject(refreshError);
      }
    }

    // Direct auth endpoint unauthorized -> just reject
    if (status === 401 && isAuthRequest) {
      return Promise.reject(error);
    }

    // Other error toasts
    // For public routes, let the calling component handle user-facing errors
    if (status >= 500 && !isPublicRoute) {
      // 
    } else if (status === 403) {
      const isSelfHrmLookup = originalRequest.url?.includes('/work-calendar') || 
                               originalRequest.url?.includes('/attendance/self-history') || 
                               originalRequest.url?.includes('/leave/self/');
      // Optional probes: callers catch and degrade; do not toast as hard denials.
      const isOptionalCreditBalanceLookup = originalRequest.url?.includes('/credit-balance');
      if (!isSelfHrmLookup && !isOptionalCreditBalanceLookup && !originalRequest.skipErrorToast) {
        toast.error('Access denied. You do not have permission to perform this action.');
      }
    } else if (status === 404) {
      // Skip toast for expected 404s (employee lookup for non-employees)
      const isEmployeeLookup = originalRequest.url?.includes('/employees/by-email/');
      const isChartOfAccountsLookup = originalRequest.url?.includes('/finance/chart-of-accounts/');
      const isRoleLookup = originalRequest.url?.includes('/roles/');
      const isSelfHrmLookup = originalRequest.url?.includes('/work-calendar') || 
                               originalRequest.url?.includes('/attendance/self-history') || 
                               originalRequest.url?.includes('/leave/self/') ||
                               originalRequest.url?.includes('/ess/view-profile-self');
      const isOptionalFileLookup = originalRequest.url?.includes('/files/presigned-url/');
      const isSubmissionVersionLookup = originalRequest.url?.includes('/submissions/') && originalRequest.url?.includes('/versions');
      // Optional credit-balance probes (callers already catch and default to 0)
      const isOptionalCreditBalanceLookup = originalRequest.url?.includes('/credit-balance');
      if (
        !originalRequest.skipErrorToast
        && !isEmployeeLookup
        && !isChartOfAccountsLookup
        && !isRoleLookup
        && !isSelfHrmLookup
        && !isOptionalFileLookup
        && !isSubmissionVersionLookup
        && !isOptionalCreditBalanceLookup
      ) {
        toast.error('Resource not found.');
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// APIS CONTAINED IN THIS FILE:
// ============================================
// - authAPI: Authentication operations (login, register, password reset, etc.)
// - userAPI: Basic user management operations (get, update, delete users)
// - publicAPI: Public system information and health checks
// - apiUtils: Utility functions for error handling and data validation
//
// For other domain-specific APIs, see dedicated files:
// - adminApi.js, rbacApi.js, inventoryApi.js, etc.
// ============================================

// Auth API - Now handles both authentication and user management (consolidated)
export const authAPI = {
  // Register new user
  register: (userData) => api.post('/auth/register', userData),

  // Login user
  login: (credentials) => api.post('/auth/login', credentials),

  // Logout user
  logout: (data) => api.post('/auth/logout', data),

  // Get current user profile
  getProfile: () => api.get('/auth/me'),

  // Forgot password
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),

  // Reset password
  resetPassword: (data) => api.post('/auth/reset-password', data),

  // Verify email
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),

  // Change password
  changePassword: (data) => api.post('/auth/change-password', data),

  // Refresh token (now uses HTTP-only cookies)
  refreshToken: (options = {}) => api.post('/auth/refresh-token', {
    extendSession: options.extendSession !== false
  })
};

// User API - All user management now consolidated through auth service
export const userAPI = {
  // Get all users (admin only) - routed to consolidated auth service
  getUsers: (params = {}, config = {}) => api.get('/users', { params, ...config }),

  // Merged users ∪ employees report (server-paginated)
  getAccountsReport: (params = {}) => api.get('/users/accounts-report', { params }),

  // Streamed CSV export for accounts report
  exportAccountsReport: (params = {}) => api.get('/users/accounts-report/export', {
    params,
    responseType: 'blob'
  }),

  // Get assignable users (any authenticated user) - for dropdowns like budget approvers
  getAssignableUsers: (params = {}) => api.get('/users/assignable', { params }),

  // Get user by ID - routed to consolidated auth service
  getUser: (userId) => api.get(`/users/${userId}`),

  // Create new user (admin only) - handled by consolidated auth service
  createUser: (userData) => api.post('/users', userData),

  // Update user (admin only) - routed to consolidated auth service
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),

  // Update user profile - routed to consolidated auth service
  updateProfile: (userData) => api.put('/users/me', userData),

  // Update user status (admin only) - routed to consolidated auth service
  updateUserStatus: (userId, status) => api.patch(`/users/${userId}/status`, { status }),

  // Update user role (admin only) - routed to consolidated auth service
  updateUserRole: (userId, role) => api.patch(`/users/${userId}/role`, { role }),

  // Update email verification status (admin only) - routed to consolidated auth service
  updateEmailVerification: (userId, emailVerified) => api.patch(`/users/${userId}/email-verification`, { emailVerified }),

  // Delete user (admin only) - routed to consolidated auth service
  deleteUser: (userId) => api.delete(`/users/${userId}`),

  // Get user statistics (admin/manager only) - routed to consolidated auth service
  getUserStats: () => api.get('/users/stats'),

  // Bulk user operations (admin only) - handled by consolidated auth service
  bulkUpdateUsers: (userIds, updateData) => api.patch('/users/bulk', { userIds, ...updateData }),

  // Export users (admin only) - handled by consolidated auth service
  exportUsers: (format = 'csv') => api.get(`/users/export?format=${format}`, { responseType: 'blob' })
};

// Note: Admin and RBAC APIs have been moved to dedicated files:
// - adminApi.js for location management and administrative operations
// - rbacApi.js for role-based access control functionality

// Note: Inventory APIs have been moved to inventoryApi.js for better organization

// Note: Location and User Location APIs have been moved to adminApi.js

// Public API - Routed to consolidated auth service
export const publicAPI = {
  // Get public system information - routed to consolidated auth service
  getSystemInfo: () => api.get('/public/system-info'),

  // Health check - API Gateway health check
  getHealth: () => api.get('/health'),

  // Get available services - reflects consolidated architecture
  getServices: () => api.get('/health/services'),

  // API documentation for consolidated architecture
  getAPIDocs: () => api.get('/docs'),

  // Get service architecture info
  getArchitectureInfo: () => api.get('/public/architecture')
};

// Utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        status,
        message: data.message || 'An error occurred',
        errors: data.errors || []
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        status: 0,
        message: 'Network error. Please check your connection.',
        errors: []
      };
    } else {
      // Something else happened
      return {
        status: 0,
        message: error.message || 'An unexpected error occurred',
        errors: []
      };
    }
  },

  // Format validation errors
  formatValidationErrors: (errors) => {
    if (Array.isArray(errors)) {
      return errors.reduce((acc, error) => {
        acc[error.field] = error.message;
        return acc;
      }, {});
    }
    return {};
  },

  // Check if user has permission
  hasPermission: (permission) => {
    try {
      const stored = localStorage.getItem('userPermissions');
      if (!stored) return false;
      const userPermissions = JSON.parse(stored);
      if (!Array.isArray(userPermissions)) return false;

      const matches = (perm) => {
        const target = perm.toUpperCase();
        if (userPermissions.some(p => typeof p === 'string' && p.toUpperCase() === target)) return true;
        const parts = target.split(':');
        if (parts.length >= 1) {
          const serviceWildcard = `${parts[0]}:*`;
          if (userPermissions.some(p => typeof p === 'string' && p.toUpperCase() === serviceWildcard)) return true;
        }
        if (parts.length >= 2) {
          const resourceWildcard = `${parts[0]}:${parts[1]}:*`;
          if (userPermissions.some(p => typeof p === 'string' && p.toUpperCase() === resourceWildcard)) return true;
        }
        if (userPermissions.some(p => p === '*:*:*')) return true;
        return false;
      };

      if (Array.isArray(permission)) return permission.some(matches);
      return matches(permission);
    } catch {
      return false;
    }
  },

  // Format date for API
  formatDate: (date) => {
    if (!date) return null;
    return new Date(date).toISOString();
  },

  // Parse date from API
  parseDate: (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  },

  // Consolidated architecture utilities
  getServiceEndpoint: (endpoint) => {
    // All user and auth operations go through the consolidated auth service
    const consolidatedEndpoints = ['/users', '/auth', '/admin', '/public'];
    const inventoryEndpoints = ['/inventory'];

    const isConsolidated = consolidatedEndpoints.some(prefix => endpoint.startsWith(prefix));
    const isInventory = inventoryEndpoints.some(prefix => endpoint.startsWith(prefix));

    return {
      service: isConsolidated ? 'auth-service' : isInventory ? 'inventory-service' : 'api-gateway',
      endpoint: endpoint,
      consolidated: isConsolidated,
      inventory: isInventory
    };
  },

  // Handle consolidated service responses
  handleConsolidatedResponse: (response) => {
    // Add metadata about which service handled the request
    if (response.data) {
      response.data._serviceInfo = {
        service: 'consolidated-auth-service',
        timestamp: new Date().toISOString(),
        architecture: 'consolidated'
      };
    }
    return response;
  },

  // Validate user data for consolidated service
  validateUserData: (userData) => {
    const required = ['email'];
    const missing = required.filter(field => !userData[field]);

    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }

    if (userData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      throw new Error('Invalid email format');
    }

    return true;
  }
};

// Export the configured axios instance as default
export default api;

/**
 * IMPORT GUIDE:
 * 
 * For core auth and user operations:
 * import api, { authAPI, userAPI } from './api';
 * 
 * For admin operations:
 * import adminAPI from './adminApi';
 * 
 * For RBAC operations:
 * import rbacAPI from './rbacApi';
 * 
 * For inventory operations:
 * import inventoryAPI from './inventoryApi';
 * 
 * The main api instance is available for direct axios calls if needed.
 */